import { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { supabase, dbSubmitMarketplaceRequest, dbFetchMyRequest } from "./supabase";

const DIET_TYPES = ["Veg","Non-Veg","Vegan","High Protein","Keto","Diabetic-friendly","Weight Loss"];
const MEAL_TYPES = ["Breakfast","Lunch","Dinner"];
const STEPS = ["profile","plans","areas","menu"];
const STEP_LABELS = { profile:"Profile", plans:"Plans", areas:"Areas", menu:"Menu & Submit" };

const emptyPlan = () => ({ name:"", desc:"", price:"" });

const inp = {
  width:"100%",padding:"9px 12px",fontSize:13,borderRadius:8,
  border:"0.5px solid #E7E5E0",outline:"none",boxSizing:"border-box",
  fontFamily:"'DM Sans','Segoe UI',sans-serif",
};
const lbl = { display:"block",fontSize:12,color:"#78716C",marginBottom:5 };

export default function ProviderSettings() {
  const { user } = useAuth();

  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading,  setUploading]  = useState(false);
  const [step,       setStep]       = useState(0); // 0=profile,1=plans,2=areas,3=menu
  const [pendingReq, setPendingReq] = useState(null);
  const [showConfirm,setShowConfirm]= useState(false);
  const [reqNote,    setReqNote]    = useState("");
  const [submitted,  setSubmitted]  = useState(false);

  // Fields
  const [name,        setName]        = useState("");
  const [tagline,     setTagline]     = useState("");
  const [description, setDescription] = useState("");
  const [whatsapp,    setWhatsapp]    = useState("");
  const [city,        setCity]        = useState("");
  const [isListed,    setIsListed]    = useState(false);
  const [dietTypes,   setDietTypes]   = useState([]);
  const [mealTypes,   setMealTypes]   = useState([]);
  const [plans,       setPlans]       = useState([emptyPlan()]);
  const [areas,       setAreas]       = useState([]);
  const [newArea,     setNewArea]     = useState("");
  const [menuImgs,    setMenuImgs]    = useState([]);
  const [published,   setPublished]   = useState({});

  useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      supabase.from("mealbox_users").select("*").eq("id", user.id).single(),
      dbFetchMyRequest(user.id),
    ]).then(([{ data }, req]) => {
      if (data) {
        const pub = {
          name: data.name||"", tagline: data.tagline||"",
          description: data.description||"", whatsapp: data.whatsapp||"",
          city: data.city||"", is_listed: data.is_listed||false,
          diet_types: data.diet_types||[], meal_types: data.meal_types||[],
          plans: data.plans||[], areas_served: data.areas_served||[],
          menu_images: data.menu_images||[],
        };
        setPublished(pub);
        const src = req ? req.payload : pub;
        setName(src.name||pub.name||"");
        setTagline(src.tagline||pub.tagline||"");
        setDescription(src.description||pub.description||"");
        setWhatsapp(src.whatsapp||pub.whatsapp||"");
        setCity(src.city||pub.city||"");
        setIsListed(src.is_listed ?? pub.is_listed ?? false);
        setDietTypes(src.diet_types||pub.diet_types||[]);
        setMealTypes(src.meal_types||pub.meal_types||[]);
        setPlans((src.plans||pub.plans||[]).length ? (src.plans||pub.plans) : [emptyPlan()]);
        setAreas(src.areas_served||pub.areas_served||[]);
        setMenuImgs(src.menu_images||pub.menu_images||[]);
      }
      setPendingReq(req||null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user]);

  const toggleArr = (arr, setArr, val) =>
    setArr(arr.includes(val) ? arr.filter(x=>x!==val) : [...arr, val]);
  const updatePlan = (i,k,v) => setPlans(p=>p.map((pl,idx)=>idx===i?{...pl,[k]:v}:pl));
  const addPlan    = ()       => { if(plans.length<4) setPlans(p=>[...p,emptyPlan()]); };
  const removePlan = (i)      => setPlans(p=>p.filter((_,idx)=>idx!==i));
  const addArea    = ()       => { const a=newArea.trim(); if(a&&!areas.includes(a)&&areas.length<10){setAreas(p=>[...p,a]);setNewArea("");} };
  const removeArea = (a)      => setAreas(p=>p.filter(x=>x!==a));
  const removeImg  = (url)    => setMenuImgs(p=>p.filter(u=>u!==url));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file||menuImgs.length>=4) return;
    if (!file.type.startsWith("image/")) { alert("Please select an image file."); return; }
    if (file.size>5*1024*1024) { alert("Image must be under 5 MB."); return; }
    setUploading(true);
    try {
      const ext  = file.name.split(".").pop();
      const path = `menus/${user.id}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("menu-images").upload(path,file,{upsert:true});
      if (error) throw error;
      const { data:{ publicUrl } } = supabase.storage.from("menu-images").getPublicUrl(path);
      setMenuImgs(p=>[...p,publicUrl]);
    } catch(err) { alert("Upload failed: "+err.message); }
    setUploading(false);
    e.target.value="";
  };

  const buildPayload = () => ({
    name,tagline,description,whatsapp,city,is_listed:isListed,
    diet_types:dietTypes,meal_types:mealTypes,
    plans:plans.filter(p=>p.name.trim()),
    areas_served:areas,menu_images:menuImgs,
  });

  const submitRequest = async () => {
    if (!name.trim()) { alert("Business name is required."); return; }
    setSubmitting(true);
    try {
      const req = await dbSubmitMarketplaceRequest(user.id, name, buildPayload());
      setPendingReq(req);
      setShowConfirm(false);
      setSubmitted(true);
    } catch(err) { alert("Failed to submit: "+err.message); }
    setSubmitting(false);
  };

  const cancelRequest = async () => {
    if (!pendingReq) return;
    await supabase.from("mealbox_marketplace_requests")
      .update({status:"cancelled"}).eq("id",pendingReq.id);
    setName(published.name||""); setTagline(published.tagline||"");
    setDescription(published.description||""); setWhatsapp(published.whatsapp||"");
    setCity(published.city||""); setIsListed(published.is_listed||false);
    setDietTypes(published.diet_types||[]); setMealTypes(published.meal_types||[]);
    setPlans((published.plans||[]).length?published.plans:[emptyPlan()]);
    setAreas(published.areas_served||[]); setMenuImgs(published.menu_images||[]);
    setPendingReq(null); setSubmitted(false); setStep(0);
  };

  const goNext = () => { if(step<STEPS.length-1) setStep(s=>s+1); };
  const goBack = () => { if(step>0) setStep(s=>s-1); };

  // ── Validation per step ───────────────────────────────────────────────────
  const stepValid = [
    name.trim().length > 0,              // profile: name required
    plans.some(p=>p.name.trim()),        // plans: at least one plan
    areas.length > 0,                    // areas: at least one area
    true,                                // menu: optional
  ];

  const C = { Breakfast:"#BA7517", Lunch:"#1D9E75", Dinner:"#534AB7" };

  if (loading) return (
    <div style={{fontFamily:"'DM Sans','Segoe UI',sans-serif",background:"#F7F6F2",minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{color:"#78716C",fontSize:13}}>Loading settings…</div>
    </div>
  );

  // ── Success screen after submit ───────────────────────────────────────────
  if (submitted) return (
    <div className="page-wrap" style={{fontFamily:"'DM Sans','Segoe UI',sans-serif",background:"#F7F6F2",minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"#FFF",borderRadius:16,border:"0.5px solid #E7E5E0",padding:"3rem 2rem",textAlign:"center",maxWidth:400}}>
        <div style={{fontSize:48,marginBottom:16}}>🎉</div>
        <h2 style={{fontSize:18,fontWeight:600,margin:"0 0 8px"}}>Request sent!</h2>
        <p style={{fontSize:13,color:"#78716C",lineHeight:1.7,marginBottom:24}}>
          Your profile has been submitted for review. The admin will approve it and your service will appear on <strong>DietFoodDelivery.in</strong> shortly.
        </p>
        <div style={{background:"#FAEEDA",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#633806",marginBottom:20,textAlign:"left"}}>
          ⏳ Usually approved within a few hours. You'll be listed automatically after approval.
        </div>
        <button onClick={()=>{setSubmitted(false);setStep(0);}}
          style={{width:"100%",padding:"10px",fontSize:13,fontWeight:500,borderRadius:8,border:"none",background:"#1C1917",color:"#FFF",cursor:"pointer"}}>
          Back to settings
        </button>
      </div>
    </div>
  );

  return (
    <div className="page-wrap" style={{fontFamily:"'DM Sans','Segoe UI',sans-serif",background:"#F7F6F2",minHeight:"100vh"}}>

      {/* Header */}
      <div style={{marginBottom:"1.5rem"}}>
        <h1 style={{fontSize:22,fontWeight:600,letterSpacing:"-0.02em",margin:0}}>Marketplace Profile</h1>
        <p style={{fontSize:13,color:"#78716C",margin:"4px 0 0"}}>How your service appears on DietFoodDelivery.in</p>
      </div>

      {/* Pending request banner */}
      {pendingReq && (
        <div style={{background:"#FAEEDA",border:"0.5px solid #EF9F27",borderRadius:12,padding:"1rem 1.25rem",marginBottom:"1.25rem",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
          <div>
            <div style={{fontWeight:500,fontSize:13,color:"#633806"}}>⏳ Changes pending admin approval</div>
            <div style={{fontSize:12,color:"#78716C",marginTop:2}}>
              Submitted {new Date(pendingReq.submitted_at).toLocaleString("en-IN",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}
            </div>
          </div>
          <button onClick={cancelRequest}
            style={{fontSize:12,padding:"6px 14px",borderRadius:8,border:"0.5px solid #EF9F27",background:"#FFF",color:"#633806",cursor:"pointer",whiteSpace:"nowrap"}}>
            Cancel request
          </button>
        </div>
      )}

      {/* Listed status */}
      <div style={{background:isListed?"#EAF3DE":"#F7F6F2",border:`0.5px solid ${isListed?"#9FE1CB":"#E7E5E0"}`,borderRadius:12,padding:"12px 16px",marginBottom:"1.25rem",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
        <div style={{fontSize:13,color:"#1C1917"}}>
          {isListed ? "✓ Set to list on DietFoodDelivery.in" : "Not listed on marketplace"}
          <div style={{fontSize:11,color:"#78716C",marginTop:2}}>
            {isListed ? "After approval, customers can find you." : "Toggle on to request listing after editing."}
          </div>
        </div>
        <button onClick={()=>setIsListed(l=>!l)} style={{
          padding:"6px 16px",fontSize:13,fontWeight:500,borderRadius:20,border:"none",cursor:"pointer",
          background:isListed?"#1C1917":"#E7E5E0",color:isListed?"#FFF":"#78716C",whiteSpace:"nowrap",
        }}>
          {isListed?"Listed ✓":"Enable"}
        </button>
      </div>

      {/* ── Step progress bar ── */}
      <div style={{display:"flex",gap:0,marginBottom:"1.5rem",background:"#FFF",borderRadius:12,border:"0.5px solid #E7E5E0",overflow:"hidden"}}>
        {STEPS.map((s,i) => {
          const done    = i < step;
          const current = i === step;
          return (
            <button key={s} onClick={()=>setStep(i)} style={{
              flex:1,padding:"12px 8px",border:"none",borderRight:i<STEPS.length-1?"0.5px solid #E7E5E0":"none",
              background: current?"#1C1917": done?"#F3FAF6":"#FFF",
              color: current?"#FFF": done?"#27500A":"#78716C",
              cursor:"pointer",fontSize:12,fontWeight:current?500:400,
              display:"flex",flexDirection:"column",alignItems:"center",gap:4,
            }}>
              <span style={{fontSize:16}}>{done?"✓":i+1}</span>
              <span>{STEP_LABELS[s]}</span>
            </button>
          );
        })}
      </div>

      {/* ── Step content ── */}
      <div style={{background:"#FFF",borderRadius:12,border:"0.5px solid #E7E5E0",padding:"1.5rem",marginBottom:"1rem"}}>

        {/* STEP 0: PROFILE */}
        {step===0 && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18}}>
            <div style={{gridColumn:"1/-1"}}>
              <label style={lbl}>Business name *</label>
              <input value={name} onChange={e=>setName(e.target.value)} style={{...inp,border:`0.5px solid ${!name.trim()?"#F09595":"#E7E5E0"}`}} placeholder="e.g. Spice Kitchen"/>
              {!name.trim() && <div style={{fontSize:11,color:"#A32D2D",marginTop:4}}>Required</div>}
            </div>
            <div style={{gridColumn:"1/-1"}}>
              <label style={lbl}>Tagline <span style={{color:"#A8A29E",fontSize:11}}>(shown on your card — max 100 chars)</span></label>
              <input value={tagline} onChange={e=>setTagline(e.target.value)} maxLength={100}
                placeholder="e.g. Fresh South Indian meals delivered daily" style={inp}/>
              <div style={{fontSize:11,color:"#A8A29E",marginTop:4,textAlign:"right"}}>{tagline.length}/100</div>
            </div>
            <div style={{gridColumn:"1/-1"}}>
              <label style={lbl}>Description <span style={{color:"#A8A29E",fontSize:11}}>(About tab on marketplace — max 600 chars)</span></label>
              <textarea value={description} onChange={e=>setDescription(e.target.value)} rows={4} maxLength={600}
                placeholder="Tell customers about your service, ingredients, story…"
                style={{...inp,resize:"vertical"}}/>
              <div style={{fontSize:11,color:"#A8A29E",marginTop:4,textAlign:"right"}}>{description.length}/600</div>
            </div>
            <div>
              <label style={lbl}>WhatsApp number <span style={{color:"#A8A29E",fontSize:11}}>(with country code)</span></label>
              <input value={whatsapp} onChange={e=>setWhatsapp(e.target.value)} placeholder="919XXXXXXXXX" style={inp}/>
            </div>
            <div>
              <label style={lbl}>City</label>
              <input value={city} onChange={e=>setCity(e.target.value)} placeholder="e.g. Chennai" style={inp}/>
            </div>
            <div style={{gridColumn:"1/-1"}}>
              <label style={lbl}>Diet type tags <span style={{color:"#A8A29E",fontSize:11}}>(select all that apply)</span></label>
              <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
                {DIET_TYPES.map(d=>{
                  const on=dietTypes.includes(d);
                  return <button key={d} onClick={()=>toggleArr(dietTypes,setDietTypes,d)} style={{padding:"6px 14px",borderRadius:20,fontSize:13,cursor:"pointer",border:`0.5px solid ${on?"#1D9E75":"#E7E5E0"}`,background:on?"#EAF3DE":"#FFF",color:on?"#1D9E75":"#78716C",fontWeight:on?500:400}}>{on?"✓ ":""}{d}</button>;
                })}
              </div>
            </div>
            <div style={{gridColumn:"1/-1"}}>
              <label style={lbl}>Meal slots offered</label>
              <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                {MEAL_TYPES.map(m=>{
                  const on=mealTypes.includes(m);
                  return <button key={m} onClick={()=>toggleArr(mealTypes,setMealTypes,m)} style={{padding:"7px 18px",borderRadius:8,fontSize:13,cursor:"pointer",border:`0.5px solid ${on?C[m]:"#E7E5E0"}`,background:on?`${C[m]}18`:"#FFF",color:on?C[m]:"#78716C",fontWeight:on?500:400}}>{on?"✓ ":""}{m}</button>;
                })}
              </div>
            </div>
          </div>
        )}

        {/* STEP 1: PLANS */}
        {step===1 && (
          <div>
            <div style={{fontSize:13,color:"#78716C",marginBottom:"1.25rem"}}>
              Add up to 4 meal plans with name, description and pricing. At least one plan is required.
            </div>
            {plans.map((p,i)=>(
              <div key={i} style={{background:"#F7F6F2",borderRadius:10,padding:"1rem",marginBottom:12,position:"relative"}}>
                <div style={{fontSize:12,fontWeight:500,color:"#78716C",marginBottom:10}}>Plan {i+1}</div>
                {plans.length>1 && <button onClick={()=>removePlan(i)} style={{position:"absolute",top:10,right:10,background:"#FCEBEB",border:"none",borderRadius:6,padding:"3px 8px",fontSize:11,color:"#A32D2D",cursor:"pointer"}}>Remove</button>}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={lbl}>Plan name *</label>
                    <input value={p.name} onChange={e=>updatePlan(i,"name",e.target.value)} placeholder="e.g. Weekly Plan" style={inp}/>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={lbl}>Description</label>
                    <input value={p.desc} onChange={e=>updatePlan(i,"desc",e.target.value)} placeholder="e.g. 5 days · Lunch + Dinner" style={inp}/>
                  </div>
                  <div>
                    <label style={lbl}>Price</label>
                    <input value={p.price} onChange={e=>updatePlan(i,"price",e.target.value)} placeholder="e.g. ₹1,400" style={inp}/>
                  </div>
                </div>
              </div>
            ))}
            {plans.length<4 && (
              <button onClick={addPlan} style={{padding:"8px 18px",fontSize:13,borderRadius:8,border:"0.5px dashed #E7E5E0",background:"transparent",color:"#78716C",cursor:"pointer",width:"100%"}}>
                + Add plan ({plans.length}/4)
              </button>
            )}
          </div>
        )}

        {/* STEP 2: AREAS */}
        {step===2 && (
          <div>
            <div style={{fontSize:13,color:"#78716C",marginBottom:"1.25rem"}}>
              Add up to 10 delivery areas. Customers search and filter by these names. At least one area is required.
            </div>
            <div style={{display:"flex",gap:8,marginBottom:14}}>
              <input value={newArea} onChange={e=>setNewArea(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addArea()}
                placeholder="Type area name and press Enter or Add" style={{...inp,flex:1}}/>
              <button onClick={addArea} disabled={areas.length>=10}
                style={{padding:"9px 16px",fontSize:13,borderRadius:8,border:"none",background:"#1C1917",color:"#FFF",cursor:"pointer",opacity:areas.length>=10?0.5:1,whiteSpace:"nowrap"}}>
                Add
              </button>
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:7,marginBottom:10}}>
              {areas.map(a=>(
                <div key={a} style={{display:"flex",alignItems:"center",gap:5,background:"#EAF3DE",borderRadius:20,padding:"5px 12px",fontSize:13,color:"#1D9E75",fontWeight:500}}>
                  📍 {a}
                  <button onClick={()=>removeArea(a)} style={{background:"none",border:"none",cursor:"pointer",color:"#A32D2D",fontSize:14,lineHeight:1,padding:"0 0 0 4px"}}>✕</button>
                </div>
              ))}
              {areas.length===0 && <span style={{fontSize:13,color:"#A8A29E"}}>No areas added yet</span>}
            </div>
            <div style={{fontSize:12,color:"#A8A29E"}}>{areas.length}/10 areas</div>
          </div>
        )}

        {/* STEP 3: MENU + SUBMIT */}
        {step===3 && (
          <div>
            <div style={{fontSize:13,color:"#78716C",marginBottom:"1.25rem"}}>
              Upload up to 4 menu photos. These appear when customers click "View Menu". <span style={{color:"#A8A29E"}}>(Optional)</span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12,marginBottom:12}}>
              {menuImgs.map((url,i)=>(
                <div key={i} style={{position:"relative",borderRadius:10,overflow:"hidden",aspectRatio:"4/3",background:"#F7F6F2"}}>
                  <img src={url} alt={`Menu ${i+1}`} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  <button onClick={()=>removeImg(url)} style={{position:"absolute",top:6,right:6,background:"rgba(0,0,0,0.55)",border:"none",borderRadius:50,width:26,height:26,color:"#FFF",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
                </div>
              ))}
              {menuImgs.length<4 && (
                <label style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8,borderRadius:10,border:"1.5px dashed #E7E5E0",aspectRatio:"4/3",cursor:"pointer",background:"#F7F6F2",color:"#78716C",fontSize:13}}>
                  {uploading?"Uploading…":<><span style={{fontSize:24}}>📷</span><span>Upload photo</span><span style={{fontSize:11,color:"#A8A29E"}}>JPG/PNG · max 5 MB</span></>}
                  <input type="file" accept="image/*" onChange={uploadImage} style={{display:"none"}} disabled={uploading}/>
                </label>
              )}
            </div>
            <div style={{fontSize:12,color:"#A8A29E",marginBottom:20}}>{menuImgs.length}/4 photos</div>

            {/* Summary before submit */}
            <div style={{background:"#F7F6F2",borderRadius:12,padding:"1rem 1.25rem",marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:500,color:"#1C1917",marginBottom:12}}>Review your profile</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 20px",fontSize:12}}>
                {[
                  {label:"Business name", value:name||"—"},
                  {label:"City",          value:city||"—"},
                  {label:"WhatsApp",      value:whatsapp||"—"},
                  {label:"Listed",        value:isListed?"Yes, request listing":"No"},
                  {label:"Diet tags",     value:(dietTypes||[]).join(", ")||"—"},
                  {label:"Meal slots",    value:(mealTypes||[]).join(", ")||"—"},
                  {label:"Plans",         value:`${plans.filter(p=>p.name.trim()).length} plan(s)`},
                  {label:"Areas",         value:`${areas.length} area(s)`},
                  {label:"Menu photos",   value:`${menuImgs.length} photo(s)`},
                ].map(r=>(
                  <div key={r.label}>
                    <span style={{color:"#78716C"}}>{r.label}: </span>
                    <span style={{color:"#1C1917",fontWeight:500}}>{r.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Navigation buttons ── */}
      <div style={{display:"flex",gap:10,justifyContent:"space-between",alignItems:"center"}}>
        <button onClick={goBack} disabled={step===0}
          style={{padding:"10px 24px",fontSize:13,borderRadius:8,border:"0.5px solid #E7E5E0",background:"#FFF",color:"#44403C",cursor:step===0?"default":"pointer",opacity:step===0?0.4:1}}>
          ← Back
        </button>

        {step < STEPS.length-1 ? (
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            {!stepValid[step] && (
              <span style={{fontSize:12,color:"#A8A29E"}}>
                {step===0?"Enter business name to continue":step===1?"Add at least one plan":"Add at least one delivery area"}
              </span>
            )}
            <button onClick={goNext} disabled={!stepValid[step]}
              style={{padding:"10px 28px",fontSize:13,fontWeight:500,borderRadius:8,border:"none",
                background:stepValid[step]?"#1C1917":"#E7E5E0",
                color:stepValid[step]?"#FFF":"#A8A29E",
                cursor:stepValid[step]?"pointer":"default"}}>
              Next →
            </button>
          </div>
        ) : (
          <button onClick={()=>setShowConfirm(true)}
            style={{padding:"10px 28px",fontSize:13,fontWeight:500,borderRadius:8,border:"none",background:"#1D9E75",color:"#FFF",cursor:"pointer"}}>
            🚀 Send for approval
          </button>
        )}
      </div>

      {/* ── Confirm modal ── */}
      {showConfirm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,fontFamily:"'DM Sans','Segoe UI',sans-serif"}}>
          <div className="modal-sm" style={{background:"#FFF",borderRadius:16,padding:"2rem"}}>
            <h3 style={{fontSize:16,fontWeight:600,margin:"0 0 8px"}}>Send for admin approval?</h3>
            <p style={{fontSize:13,color:"#78716C",marginBottom:14,lineHeight:1.6}}>
              Your profile will be reviewed by the admin. Once approved, your service will be live on <strong>DietFoodDelivery.in</strong> instantly.
              {pendingReq&&" This will replace your previous pending request."}
            </p>
            <div style={{marginBottom:14}}>
              <label style={lbl}>Note for admin <span style={{color:"#A8A29E"}}>(optional)</span></label>
              <textarea value={reqNote} onChange={e=>setReqNote(e.target.value)} rows={2}
                placeholder="e.g. New provider, first time listing"
                style={{...inp,resize:"none"}}/>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={submitRequest} disabled={submitting}
                style={{flex:1,padding:"10px",fontSize:13,fontWeight:500,borderRadius:8,border:"none",background:submitting?"#78716C":"#1C1917",color:"#FFF",cursor:submitting?"default":"pointer"}}>
                {submitting?"Submitting…":"Send request"}
              </button>
              <button onClick={()=>setShowConfirm(false)}
                style={{padding:"10px 18px",fontSize:13,borderRadius:8,border:"0.5px solid #E7E5E0",background:"#FFF",cursor:"pointer"}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
